import { parseTrackpointsGpx } from "../engine/popularity";

/** Spacing kept between stored points; matching radius is 20 m, so 10 m loses nothing. */
export const SPACING_M = 10;

export interface StoredRuns {
  runs: number;
  /** Flat lat, lon pairs. */
  pts: Float64Array;
}

const R = 111_195;

/** Parses GPX text and thins every segment to roughly `SPACING_M` between points. */
export function gpxToPoints(xml: string): Float64Array {
  const out: number[] = [];
  for (const seg of parseTrackpointsGpx(xml)) {
    let last: { lat: number; lon: number } | null = null;
    for (const p of seg) {
      if (last) {
        const dy = (p.lat - last.lat) * R;
        const dx = (p.lon - last.lon) * R * Math.cos((p.lat * Math.PI) / 180);
        if (Math.hypot(dx, dy) < SPACING_M) continue;
      }
      out.push(p.lat, p.lon);
      last = p;
    }
  }
  return Float64Array.from(out);
}

export function mergeRuns(a: StoredRuns, b: StoredRuns): StoredRuns {
  const pts = new Float64Array(a.pts.length + b.pts.length);
  pts.set(a.pts);
  pts.set(b.pts, a.pts.length);
  return { runs: a.runs + b.runs, pts };
}

// ---------------------------------------------------------------------------------------------
// Persistence: IndexedDB, falling back to localStorage. Everything stays on this device.

const DB = "makeatrail";
const STORE = "kv";
const KEY = "myruns:v1";
const LS_KEY = "makeatrail:myruns:v1";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idb<T>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>) {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const r = f(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(r.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function loadRuns(): Promise<StoredRuns | null> {
  try {
    const v = await idb<StoredRuns | undefined>("readonly", (s) => s.get(KEY));
    if (v && v.pts instanceof Float64Array) return v;
  } catch {
    // Fall through to localStorage.
  }
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { runs: number; pts: number[] };
      return { runs: p.runs, pts: Float64Array.from(p.pts) };
    }
  } catch {
    // Nothing stored.
  }
  return null;
}

export async function saveRuns(r: StoredRuns | null): Promise<void> {
  try {
    await (r ? idb("readwrite", (s) => s.put(r, KEY)) : idb("readwrite", (s) => s.delete(KEY)));
    localStorage.removeItem(LS_KEY);
    return;
  } catch {
    // IndexedDB unavailable (private mode, etc.): small sets fit in localStorage.
  }
  try {
    if (!r) localStorage.removeItem(LS_KEY);
    else
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ runs: r.runs, pts: Array.from(r.pts, (x) => Math.round(x * 1e6) / 1e6) }),
      );
  } catch {
    // Not persisted; still works for this session.
  }
}
